/**
 * PuzzleSeedForm — admin control to kick off a background puzzle-seed job.
 *
 * Sits inside the AdminDashboard's Gamify Config card. Lets the admin:
 *  - Pick scope (subject / class / difficulty / variants_per_topic)
 *  - Fire a background job that pre-warms the puzzle_cache via the LLM
 *  - Watch the last N jobs with live progress polling
 */
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Play, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { apiGet, apiPost } from '@/lib/api';
import { toast } from 'sonner';

const SUBJECTS = [
  { id: '', label: 'All subjects' },
  { id: 'math', label: 'Math' },
  { id: 'physics', label: 'Physics' },
  { id: 'chemistry', label: 'Chemistry' },
  { id: 'biology', label: 'Biology' },
  { id: 'english', label: 'English' },
  { id: 'hindi', label: 'Hindi' },
  { id: 'social_studies', label: 'Social Studies' },
  { id: 'history', label: 'History' },
  { id: 'geography', label: 'Geography' },
  { id: 'computer', label: 'Computer Science' },
  { id: 'logic', label: 'Logic & Reasoning' },
];
const DIFFS = [
  { id: '', label: 'All difficulties' },
  { id: 'easy', label: 'Easy' },
  { id: 'medium', label: 'Medium' },
  { id: 'hard', label: 'Hard' },
];
const CLASSES = ['', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

export default function PuzzleSeedForm() {
  const [subject, setSubject] = useState('math');
  const [classLevel, setClassLevel] = useState('8');
  const [difficulty, setDifficulty] = useState('easy');
  const [variants, setVariants] = useState(5);
  const [maxTotal, setMaxTotal] = useState(25);
  const [starting, setStarting] = useState(false);
  const [jobs, setJobs] = useState([]);

  const loadJobs = async () => {
    try {
      const res = await apiGet('/admin/puzzles/seed/jobs?limit=10');
      setJobs(res.jobs || []);
    } catch {}
  };
  useEffect(() => {
    loadJobs();
    const t = setInterval(() => {
      // Only poll if there are running jobs
      if (jobs.some(j => j.status === 'running')) loadJobs();
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [jobs.map(j => j.status).join(',')]);

  const kickoff = async () => {
    setStarting(true);
    try {
      const payload = {
        subject: subject || null,
        class_level: classLevel || null,
        difficulty: difficulty || null,
        variants_per_topic: parseInt(variants) || 25,
        max_total: parseInt(maxTotal) || null,
      };
      const res = await apiPost('/admin/puzzles/seed', payload);
      toast.success(`Seed job started · ${res.target_count} targets`);
      loadJobs();
    } catch (e) {
      toast.error((e?.message || '').replace(/^HTTP \d+:\s*/, '') || 'Failed to start seed job');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="puzzle-seed-form">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div>
          <Label className="text-[10px] font-heading text-slate-500">Subject</Label>
          <select value={subject} onChange={e => setSubject(e.target.value)}
            data-testid="seed-subject"
            className="w-full px-2 py-2 rounded-lg border border-slate-200 text-xs font-body bg-white">
            {SUBJECTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px] font-heading text-slate-500">Class</Label>
          <select value={classLevel} onChange={e => setClassLevel(e.target.value)}
            data-testid="seed-class"
            className="w-full px-2 py-2 rounded-lg border border-slate-200 text-xs font-body bg-white">
            {CLASSES.map(c => <option key={c} value={c}>{c === '' ? 'All classes' : `Class ${c}`}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px] font-heading text-slate-500">Difficulty</Label>
          <select value={difficulty} onChange={e => setDifficulty(e.target.value)}
            data-testid="seed-difficulty"
            className="w-full px-2 py-2 rounded-lg border border-slate-200 text-xs font-body bg-white">
            {DIFFS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px] font-heading text-slate-500">Variants/topic</Label>
          <Input type="number" min="1" max="100" value={variants}
            data-testid="seed-variants"
            onChange={e => setVariants(e.target.value)}
            className="rounded-lg text-xs font-body" />
        </div>
        <div>
          <Label className="text-[10px] font-heading text-slate-500">Max total (safety)</Label>
          <Input type="number" min="1" max="5000" value={maxTotal}
            data-testid="seed-max-total"
            onChange={e => setMaxTotal(e.target.value)}
            className="rounded-lg text-xs font-body" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button onClick={kickoff} disabled={starting} data-testid="seed-start-btn"
          className="bg-violet-500 hover:bg-violet-600 text-white font-heading text-xs">
          {starting ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Queuing…</> : <><Play className="w-3.5 h-3.5 mr-1.5" />Start seed job</>}
        </Button>
        <Button onClick={loadJobs} variant="outline" size="sm" className="font-heading text-xs" data-testid="seed-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Jobs list */}
      {jobs.length > 0 && (
        <Card className="p-3 bg-slate-50 border-slate-200" data-testid="seed-jobs-list">
          <p className="text-[10px] font-heading text-slate-500 uppercase tracking-wide mb-2">Recent jobs</p>
          <div className="space-y-1.5">
            {jobs.map(j => {
              const done = (j.completed || 0) + (j.failed || 0) + (j.skipped_cached || 0);
              const pct = j.target_count > 0 ? Math.min(100, Math.round((done / j.target_count) * 100)) : 0;
              const Icon = j.status === 'done' ? CheckCircle2 : j.status === 'running' ? Loader2 : AlertCircle;
              return (
                <div key={j.job_id} data-testid={`seed-job-${j.job_id}`} className="flex items-center gap-3 px-2 py-1.5 rounded-lg bg-white border border-slate-100">
                  <Icon className={`w-4 h-4 flex-shrink-0 ${j.status === 'running' ? 'animate-spin text-violet-500' : j.status === 'done' ? 'text-emerald-600' : 'text-slate-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-body text-slate-600 flex flex-wrap items-center gap-x-2">
                      <span className="font-mono text-[10px] text-slate-400">{j.job_id}</span>
                      <span>
                        {j.scope?.subject || 'all'}/{j.scope?.class_level || 'all'}/{j.scope?.difficulty || 'all'} × {j.scope?.variants_per_topic || '?'}
                      </span>
                      <span className="text-slate-400">
                        ✓{j.completed || 0} · ⤴{j.skipped_cached || 0} · ✗{j.failed || 0} / {j.target_count}
                      </span>
                    </div>
                    <div className="h-1 bg-slate-100 rounded-full mt-1 overflow-hidden">
                      <div className={`h-full ${j.status === 'done' ? 'bg-emerald-500' : 'bg-violet-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
